/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, keyframes } from '@emotion/core';
import { Button, Spin, Empty, Typography, Carousel } from 'antd';
import { useIntl } from 'react-intl';
import { useProjectId } from '~src/hooks/useProjectId';
import { useTemplates } from '~src/hooks/api-hooks/useTemplates';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DbProject } from '~src/types/db';

const { Title, Paragraph } = Typography;

interface TemplateSelectionStepProps {
  onTemplateSelect: (templateId: string) => void;
  onSkip: () => void;
}

export const TemplateSelectionStep: React.FC<TemplateSelectionStepProps> = ({
  onTemplateSelect,
  onSkip,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();

  const templatesQuery = useTemplates(projectId);

  if (templatesQuery.isLoading) {
    return (
      <div css={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <FontAwesomeIcon
          icon={['fad', 'sliders-h-square']}
          css={styles.welcomeIcon}
        />
        <Title level={3} css={styles.title}>
          {intl.formatMessage({ id: 'project-builder-welcome' })}
        </Title>
        <Paragraph css={styles.description}>
          {intl.formatMessage({ id: 'project-builder-description' })}
        </Paragraph>
      </div>

      <div css={styles.content}>
        <div css={styles.templateSection}>
          <Title level={4} css={styles.sectionTitle}>
            {intl.formatMessage({ id: 'available-templates' })}
          </Title>

          {!templatesQuery.data || templatesQuery.data.length === 0 ? (
            <Empty
              description={intl.formatMessage({ id: 'no-templates' })}
              css={styles.empty}
            />
          ) : templatesQuery.data.length > 5 ? (
            <Carousel arrows dots css={styles.carousel}>
              {Array.from({
                length: Math.ceil(templatesQuery.data.length / 5),
              }).map((_, pageIndex) => (
                <div key={pageIndex}>
                  <div css={styles.templateGrid}>
                    {templatesQuery.data
                      .slice(pageIndex * 5, (pageIndex + 1) * 5)
                      .map((template: DbProject) => (
                        <Button
                          key={template.id}
                          type="default"
                          size="large"
                          onClick={() => onTemplateSelect(String(template.id))}
                          css={styles.templateButton}>
                          <div css={styles.templateButtonContent}>
                            <FontAwesomeIcon
                              icon={['fad', 'layer-group']}
                              css={styles.templateIcon}
                            />
                            <div css={styles.templateInfo}>
                              <div css={styles.templateName}>
                                {template.name}
                              </div>
                              {template.description && (
                                <div css={styles.templateDescription}>
                                  {template.description}
                                </div>
                              )}
                            </div>
                            <FontAwesomeIcon
                              icon={['fas', 'chevron-right']}
                              css={styles.chevron}
                            />
                          </div>
                        </Button>
                      ))}
                  </div>
                </div>
              ))}
            </Carousel>
          ) : (
            <div css={styles.templateGrid}>
              {templatesQuery.data.map((template: DbProject) => (
                <Button
                  key={template.id}
                  type="default"
                  size="large"
                  onClick={() => onTemplateSelect(String(template.id))}
                  css={styles.templateButton}>
                  <div css={styles.templateButtonContent}>
                    <FontAwesomeIcon
                      icon={['fad', 'layer-group']}
                      css={styles.templateIcon}
                    />
                    <div css={styles.templateInfo}>
                      <div css={styles.templateName}>{template.name}</div>
                      {template.description && (
                        <div css={styles.templateDescription}>
                          {template.description}
                        </div>
                      )}
                    </div>
                    <FontAwesomeIcon
                      icon={['fas', 'chevron-right']}
                      css={styles.chevron}
                    />
                  </div>
                </Button>
              ))}
            </div>
          )}
        </div>

        <div css={styles.divider}>
          <span css={styles.dividerText}>
            {intl.formatMessage({ id: 'or' })}
          </span>
        </div>

        <div css={styles.skipSection}>
          <Button
            type="dashed"
            size="large"
            onClick={onSkip}
            css={styles.skipButton}>
            <FontAwesomeIcon
              icon={['fad', 'pencil-ruler']}
              style={{ marginRight: 8 }}
            />
            {intl.formatMessage({ id: 'skip-start-from-scratch' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    animation: `${fadeIn} 0.4s ease-out`,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  header: {
    textAlign: 'center',
    marginBottom: 24,
    paddingTop: 12,
  },
  welcomeIcon: {
    fontSize: 48,
    color: theme.colors.primary,
    marginBottom: 12,
  },
  title: {
    marginBottom: 12,
    color: theme.colors.text,
  },
  description: {
    fontSize: 16,
    color: theme.colors.textSecondary,
    marginBottom: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },
  templateSection: {
    flex: 1,
    maxWidth: '75%',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 16,
    color: theme.colors.text,
  },
  carousel: {
    '.ant-carousel .slick-prev': {
      left: -40,
      zIndex: 1,
    },
    '.ant-carousel .slick-next': {
      right: -40,
      zIndex: 1,
    },
    '.ant-carousel .slick-dots': {
      bottom: -30,
    },
  },
  templateGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  templateButton: {
    width: '100%',
    height: 'auto',
    padding: '16px 20px',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    border: `1px solid ${theme.colors.border}`,
    '&:hover': {
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.primaryLight,
      transform: 'translateX(4px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  templateButtonContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 16,
  },
  templateIcon: {
    fontSize: 24,
    color: theme.colors.primary,
    flexShrink: 0,
  },
  templateInfo: {
    flex: 1,
    textAlign: 'left',
  },
  templateName: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.colors.text,
    marginBottom: 4,
  },
  templateDescription: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  chevron: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    transition: 'transform 0.2s ease',
    flexShrink: 0,
  },
  divider: {
    textAlign: 'center',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: theme.colors.border,
    },
  },
  dividerText: {
    position: 'relative',
    backgroundColor: theme.colors.background,
    padding: '0 16px',
    color: theme.colors.textSecondary,
    fontSize: 14,
    fontWeight: 500,
  },
  skipSection: {
    display: 'flex',
    justifyContent: 'center',
  },
  skipButton: {
    height: 48,
    fontSize: 16,
    padding: '0 32px',
    borderRadius: 8,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.colors.primary,
      color: theme.colors.primary,
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
  },
  empty: {
    padding: '40px 0',
  },
});
